import { useEffect, useRef } from 'react';
import type { ComponentPropsWithRef, FormEvent } from 'react';
import { IconSearch, IconX } from '@tabler/icons-react';

import { useTranslation } from '../../../i18n/LocaleProvider';
import { cn } from '../../../lib/cn';
import { useDebouncedValue } from '../../../lib/useRemoteSearch';
import { IconButton } from '../../core/IconButton';
import { Spinner } from '../../core/Spinner';
import { Field, fieldDescribedBy } from './Field';
import { useField } from './useField';
import { SHELL_CONTROL, controlShellClass } from './controlStyles';
import type { ControlSize } from './controlStyles';

/* =============================================================================
   InputSearch — a free-text input (§10) composed with the house icon button
   (§3) and a leading magnifier.
   -----------------------------------------------------------------------------
   Two search paths share one value:

     - Typing is debounced (`debounceMs`, default 300). The debounce effect
       compares the settled value against the last value it actually searched,
       so the mount pass of the double-invoked StrictMode effect fires nothing.
     - Pressing Enter submits the form natively and searches immediately,
       bypassing the wait. The form holds exactly one text-blocking control (the
       search input) and its only other control is `type="button"`, which is
       what makes the browser's implicit-submission path apply — no keydown
       handler is involved.
   ============================================================================= */

export interface InputSearchProps extends Omit<ComponentPropsWithRef<'input'>, 'size' | 'value' | 'onChange'> {
  id: string;
  label?: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  /** Fired with the settled value — on submit immediately, otherwise debounced. */
  onSearch: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  /** Wait before a typed term is searched. Default 300. */
  debounceMs?: number;
  /** Default 'Search'. The field can render without a visible label, so this
   *  is the input's only accessible name in that case. */
  ariaLabel?: string;
  required?: boolean;
  /** Consumer-owned validation message. Wins over the hook's own. */
  error?: string | null;
  /** Inline-save latch: raises the success state and the "Saved!" message. */
  success?: boolean;
  successMessage?: string;
  size?: ControlSize;
  disabled?: boolean;
  /** Shows the spinner in place of the magnifier and sets `aria-busy`. */
  loading?: boolean;
  /** Bumped by the form on each submit attempt, revealing every field's error. */
  submitCount?: number;
}

export function InputSearch({
  id,
  label,
  description,
  value,
  onChange,
  onSearch,
  onClear,
  placeholder,
  debounceMs = 300,
  ariaLabel,
  required,
  error,
  success,
  successMessage,
  size = 'md',
  disabled = false,
  loading = false,
  submitCount,
  onBlur,
  className,
  ...rest
}: InputSearchProps) {
  const { t } = useTranslation();
  const field = useField({ value, onChange, required, submitCount, success });
  const message = error ?? field.error;
  const invalid = Boolean(message);
  const savedMessage = field.success ? (successMessage ?? t('ds.number.saved')) : undefined;
  const describedBy = fieldDescribedBy(id, {
    description: Boolean(description),
    message: Boolean(message ?? savedMessage),
  });

  const debounced = useDebouncedValue(value, debounceMs);

  // The value the debounce has already searched. It survives the effect's
  // cleanup and its StrictMode re-run, so a pristine value searches nothing.
  const lastSearched = useRef(value);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    if (debounced === lastSearched.current) return;
    lastSearched.current = debounced;
    onSearchRef.current(debounced);
  }, [debounced]);

  /**
   * The native submit path. `lastSearched` is advanced here too, so the wait
   * already scheduled for this same value does not fire the search a second time.
   */
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    lastSearched.current = value;
    onSearch(value);
  }

  function handleClear() {
    onChange('');
    onClear?.();
    // Same guard as the submit path, for the immediate search below.
    lastSearched.current = '';
    onSearch('');
  }

  return (
    <form role="search" noValidate className="w-full min-w-0" onSubmit={handleSubmit}>
      <Field
        id={id}
        label={label}
        description={description}
        required={required}
        disabled={disabled}
        invalid={invalid}
        error={message}
        successMessage={savedMessage}
        size={size}
      >
        <div className={controlShellClass({ size, invalid, success: field.success && !invalid, disabled })}>
          {loading ? (
            // `aria-busy` on the input is the state; a second unnamed live
            // region would only blur it.
            <Spinner size="sm" tone="muted" aria-hidden="true" />
          ) : (
            <IconSearch className="size-4 shrink-0 text-muted" aria-hidden="true" />
          )}

          <input
            {...rest}
            id={id}
            type="search"
            role="searchbox"
            inputMode="search"
            autoComplete="off"
            spellCheck={false}
            /* With a visible label the label IS the accessible name; only the
               label-less field falls back to `ariaLabel`. Announcing "Search"
               over a visible "Filter users" would fail label-in-name. */
            aria-label={label ? undefined : (ariaLabel ?? t('ds.search.label'))}
            /* The form is `noValidate` and the native `required` attribute is not
               set, so the constraint is carried by `aria-required` alone. */
            aria-required={required || undefined}
            aria-invalid={invalid || undefined}
            aria-busy={loading || undefined}
            aria-describedby={describedBy}
            value={value}
            onChange={(event) => field.setValue(event.target.value)}
            onBlur={(event) => {
              // `...rest` is spread first, so the consumer's own `onBlur` would
              // otherwise be silently replaced by this one.
              onBlur?.(event);
              field.onBlur();
            }}
            placeholder={placeholder}
            disabled={disabled}
            /* The shell draws the only focus ring; this opts the inner control out. */
            data-focus-ring="none"
            /* `type="search"` renders a UA clear affordance that would duplicate
               the house clear button, so it is suppressed. */
            className={cn(SHELL_CONTROL, '[&::-webkit-search-cancel-button]:appearance-none', className)}
          />

          {value !== '' ? (
            // `z-10` is load-bearing: `Field`'s `dhd-target` overlay is a
            // positioned pseudo-element generated after the row's children, so
            // without its own stacking position this button is painted under it
            // and every click is swallowed.
            <IconButton
              size="sm"
              variant="ghost"
              label={t('ds.search.clear')}
              icon={<IconX />}
              onClick={handleClear}
              className="z-10"
            />
          ) : null}
        </div>
      </Field>
    </form>
  );
}
